import React, { useEffect, useState, useCallback } from 'react';
import { BookOpen, RefreshCw, Search, X } from 'lucide-react';
import api from './axiosConfig';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useFontSize } from './FontSizeContext';
import GLAccountTree from './GLAccountTree';
import { translateAccountDescription } from './glDefaultAccounts';

const GLLedger = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const { fontSize } = useFontSize();

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const [orgId, setOrgId] = useState(session.org_id || '');
  const [account, setAccount] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState(new Date().toISOString().substring(0, 10));
  const [rows, setRows] = useState([]);
  const [closingBalance, setClosingBalance] = useState(null);
  const [loading, setLoading] = useState(false);

  // 🎯 الحساب دلوقتي فلتر اختياري بس - من غيره التقرير بيرجع كل حركات
  // اليومية (زي دفتر يومية عام)، ولو اتحدد حساب بيتفلتر عليه ويظهر رصيد متراكم
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/gl/ledger', { params: { glaccount: account?.glaccount || undefined, org_id: orgId, from_date: fromDate || undefined, to_date: toDate } });
      setRows(res.data.data);
      setClosingBalance(res.data.closing_balance);
    } catch (err) {
      toast.error(t('common.load_error', 'Failed to load ledger'));
    } finally {
      setLoading(false);
    }
  }, [account, orgId, fromDate, toDate, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className={`flex flex-col h-full bg-surface-app uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      <GLAccountTree
        isOpen={pickerOpen}
        title={t('gl.ledger.select_account', 'Select Account')}
        orgId={orgId}
        onClose={() => setPickerOpen(false)}
        onSelect={(node) => { setAccount(node); setPickerOpen(false); }}
      />

      <div className="bg-surface-card p-3 border-b shadow-sm z-20">
        <div className="flex justify-between items-center px-4 flex-wrap gap-3">
          <h2 className="text-primary-dark font-black italic text-lg flex items-center gap-2">
            <BookOpen size={22} className="text-status-warning" />
            {t('gl.ledger.title', 'General Ledger')}
            <RefreshCw size={18} className={`cursor-pointer transition-all ${loading ? 'animate-spin text-status-warning' : 'text-primary'} ${isRTL ? 'mr-2' : 'ml-2'}`} onClick={fetchData} />
          </h2>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex flex-col">
              <label className="text-[9px] text-ink-secondary font-black">{t('gl.trial_balance.org', 'Organization')}</label>
              <input className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1 uppercase" value={orgId} onChange={e => setOrgId(e.target.value.toUpperCase())} />
            </div>
            <div className="flex flex-col">
              <label className="text-[9px] text-ink-secondary font-black">{t('gl.ledger.account', 'Account')}</label>
              <div className="flex items-center gap-2 border-b-2 border-line-subtle py-1 min-w-[160px]">
                <span
                  className={`font-bold cursor-pointer ${account ? 'text-primary' : 'text-ink-secondary italic font-normal'}`}
                  onClick={() => setPickerOpen(true)}
                >
                  {account ? `${account.glaccount} - ${translateAccountDescription(account, t)}` : t('gl.ledger.all_accounts', 'All Accounts')}
                </span>
                {account ? (
                  <X size={14} className="text-status-danger shrink-0 cursor-pointer" onClick={() => setAccount(null)} />
                ) : (
                  <Search size={14} className="text-primary shrink-0 cursor-pointer" onClick={() => setPickerOpen(true)} />
                )}
              </div>
            </div>
            <div className="flex flex-col">
              <label className="text-[9px] text-ink-secondary font-black">{t('gl.ledger.from', 'From')}</label>
              <input type="date" className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1" value={fromDate} onChange={e => setFromDate(e.target.value)} />
            </div>
            <div className="flex flex-col">
              <label className="text-[9px] text-ink-secondary font-black">{t('gl.ledger.to', 'To')}</label>
              <input type="date" className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1" value={toDate} onChange={e => setToDate(e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-surface-card border rounded-sm shadow-sm overflow-hidden">
          <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
            <thead className="bg-primary-dark text-white font-bold uppercase sticky top-0 z-10">
              <tr>
                <th className="p-3 border-x border-white/10">{t('gl.ledger.fields.date', 'Date')}</th>
                <th className="p-3 border-x border-white/10">{t('gl.ledger.fields.entry_num', 'Entry #')}</th>
                <th className="p-3 border-x border-white/10">{t('gl.ledger.fields.source', 'Source')}</th>
                {!account && <th className="p-3 border-x border-white/10">{t('gl.trial_balance.fields.account', 'Account')}</th>}
                <th className="p-3 border-x border-white/10">{t('gl.ledger.fields.description', 'Description')}</th>
                <th className="p-3 border-x border-white/10 text-center w-32">{t('gl.trial_balance.fields.debit', 'Debit')}</th>
                <th className="p-3 border-x border-white/10 text-center w-32">{t('gl.trial_balance.fields.credit', 'Credit')}</th>
                {account && <th className="p-3 border-x border-white/10 text-center w-32">{t('gl.trial_balance.fields.balance', 'Balance')}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle font-bold">
              {loading ? (
                <tr><td colSpan="8" className="p-20 text-center font-black text-ink-secondary animate-pulse tracking-[0.2em]">{t('common.syncing', 'Syncing Data...')}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="8" className="p-20 text-center font-black text-ink-secondary italic uppercase">{t('gl.ledger.no_records', 'No movements found')}</td></tr>
              ) : (
                rows.map(r => (
                  <tr key={`${r.entry_num}-${r.glaccount || ''}`} className="hover:bg-primary-soft">
                    <td className="p-3 border-x border-line-subtle text-ink-secondary">{r.entry_date ? new Date(r.entry_date).toLocaleDateString() : ''}</td>
                    <td className="p-3 border-x border-line-subtle text-primary">{r.entry_num}</td>
                    <td className="p-3 border-x border-line-subtle text-center">
                      <span className="bg-status-warning-bg text-status-warning px-2 py-0.5 rounded-full text-[9px] shadow-sm border border-status-warning-bg">
                        {r.source}
                      </span>
                    </td>
                    {!account && <td className="p-3 border-x border-line-subtle text-primary">{r.glaccount}</td>}
                    <td className="p-3 border-x border-line-subtle text-ink normal-case italic">{r.description}</td>
                    <td className="p-3 border-x border-line-subtle text-center text-status-success">{r.debit ? Number(r.debit).toLocaleString() : ''}</td>
                    <td className="p-3 border-x border-line-subtle text-center text-status-danger">{r.credit ? Number(r.credit).toLocaleString() : ''}</td>
                    {account && <td className="p-3 border-x border-line-subtle text-center text-primary">{Number(r.balance).toLocaleString()}</td>}
                  </tr>
                ))
              )}
            </tbody>
            {account && rows.length > 0 && (
              <tfoot>
                <tr className="bg-surface-panel font-black">
                  <td colSpan="6" className="p-3 border-x border-line-subtle text-right">{t('gl.ledger.closing_balance', 'Closing Balance')}</td>
                  <td className="p-3 border-x border-line-subtle text-center text-primary">{Number(closingBalance).toLocaleString()}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default GLLedger;
